import {useCallback} from 'react';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {
	useAccountInformation,
	useHandleNewAppointment,
	useMediaQueries,
	useQueryParams,
} from 'presentation/pages/private/hooks';
import {filterAndSortBookedAppointments} from '../utils/filterAndSortBookedAppointments';
import {useContractStatus} from 'presentation/hooks';
import {useLocation} from 'react-router-dom';
import {getAppointmentsEmptyDescription} from 'presentation/modules/myAppointments/utils/getAppointmentsEmptyDescription';
import {useLazyScroll} from 'presentation/hooks/useLazyScroll';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

export const useMyAppointmentsList = () => {
	const {SEARCH_PARAMS} = useQueryParams();
	const {pathname} = useLocation();
	const {isTablet} = useMediaQueries();
	const {handleNewAppointment} = useHandleNewAppointment();
	const {isValid, isContractSuspended} = useContractStatus();
	const {hideHealthHospitalComponent, odontoOnly} = useAccountInformation();
	const {bookingType} = SEARCH_PARAMS;
	const {
		data: homeData,
		refetch,
		isError: appointmentsListError,
		isLoading: appointmentsListLoading,
	} = useGetHomeQuery('always');

	const {congenereEligibility} = useCongenereEligibilityStore();

	const appointmentsList = homeData?.appointments || [];
	const appointmentsListHasValue = !!homeData?.appointments;

	const filterAppointmentsList = useCallback(() => {
		if (!appointmentsListHasValue) return [];
		return filterAndSortBookedAppointments({
			filter: bookingType,
			appointments: appointmentsList,
		});
	}, [bookingType, homeData, appointmentsListHasValue]);
	const appointmentsListFiltered = filterAppointmentsList();

	const appointmentsListFilteredEmpty = appointmentsListFiltered.length === 0;

	const isEmpty = !appointmentsListFiltered || appointmentsListFiltered.length === 0;

	const {items, scrollableContentRef, loadingNewPageState, handleVisible} = useLazyScroll({
		itemsList: appointmentsListHasValue ? appointmentsListFiltered : [],
		itemsPerPage: isTablet ? 3 : 8,
		loadingTime: 600,
	});

	const listHasCardWithFavoriteButton = items.some(
		item =>
			item.bookingType === BookingTypeEnum.HEALTH ||
			item.bookingType === BookingTypeEnum.TELEHEALTH,
	);
	const firstFavoriteCardIndex = items.findIndex(
		item =>
			item.bookingType === BookingTypeEnum.HEALTH ||
			item.bookingType === BookingTypeEnum.TELEHEALTH,
	);

	const emptyStateDescription = getAppointmentsEmptyDescription({
		isValid,
		isContractSuspended,
		odontoOnly,
	});

	const isDetailPath = pathname.includes('detalhes-do-agendamento');
	const isHistoryDetailPath = pathname.includes('detalhes-da-consulta');

	const shouldHideScheduleButton = Boolean(
		hideHealthHospitalComponent || !isValid || isContractSuspended || congenereEligibility,
	);
	const shouldHideAlertInfo = isDetailPath || isHistoryDetailPath || !isValid || odontoOnly;

	return {
		appointmentsList,
		appointmentsListFiltered,
		refresh: refetch,
		appointmentsListHasValue,
		appointmentsListError,
		appointmentsListLoading,
		appointmentsListFilteredEmpty,
		isEmpty,
		emptyStateDescription,
		shouldHideScheduleButton,
		shouldHideAlertInfo,
		isTablet,
		handleNewAppointment,
		list: items,
		scrollableContentRef,
		loadingNewPageState,
		handleVisible,
		listHasCardWithFavoriteButton,
		firstFavoriteCardIndex,
	};
};
